/**
 * DataLoader — indicador de carga de datos locales DERA/INE
 *
 * Reemplaza el modal bloqueante por un chip discreto en la esquina
 * inferior derecha que muestra el progreso sin interrumpir P1.
 * Desaparece automáticamente 1,5 s después de completarse.
 *
 * @version 2.0.0 — toast discreto (sin modal bloqueante)
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocalData } from '../hooks/useLocalData';

export interface DataLoaderProps {
  onReady?: () => void;
  onError?: (error: Error) => void;
  /** Ignorado — mantenido por compatibilidad; ya no bloquea con modal */
  modal?: boolean;
  autoLoad?: boolean;
}

export function DataLoader({ onReady, onError, autoLoad = true }: DataLoaderProps) {
  const { isChecking, isReady, isLoading, progress, error, loadData } = useLocalData();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!isChecking && !isReady && !isLoading && !error && autoLoad) loadData();
  }, [isChecking, isReady, isLoading, error, autoLoad, loadData]);

  useEffect(() => {
    if (isReady && onReady) onReady();
    if (isReady) {
      const t = setTimeout(() => setVisible(false), 1500);
      return () => clearTimeout(t);
    }
  }, [isReady, onReady]);

  useEffect(() => {
    if (error && onError) onError(error);
  }, [error, onError]);

  const pct = progress?.percent ?? 0;
  const show = visible && (!isReady || isLoading);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.95 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            bottom: '24px',   // margen estándar DS (T2b: el StepperFloating ya no existe)
            right: '24px',
            zIndex: 300,
            background: 'rgba(30,30,28,0.92)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '10px',
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            minWidth: '220px',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
            fontFamily: "'Space Grotesk', system-ui, sans-serif",
          }}
        >
          {/* Icono de base de datos animado */}
          <div style={{ flexShrink: 0, position: 'relative', width: 28, height: 28 }}>
            <svg viewBox="0 0 24 24" width={28} height={28} fill="none" stroke="#2DD4BF" strokeWidth={1.5}>
              <ellipse cx="12" cy="5" rx="9" ry="3"/>
              <path d="M3 5v4c0 1.657 4.03 3 9 3s9-1.343 9-3V5"/>
              <path d="M3 9v4c0 1.657 4.03 3 9 3s9-1.343 9-3V9"/>
              <path d="M3 13v4c0 1.657 4.03 3 9 3s9-1.343 9-3v-4"/>
            </svg>
            {isLoading && (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                style={{
                  position: 'absolute', bottom: -3, right: -3,
                  width: 12, height: 12, borderRadius: '50%',
                  border: '2px solid transparent',
                  borderTopColor: '#2DD4BF',
                  borderRightColor: '#2DD4BF',
                }}
              />
            )}
            {isReady && (
              <motion.div
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                style={{
                  position: 'absolute', bottom: -3, right: -3,
                  width: 12, height: 12, borderRadius: '50%',
                  background: '#34D399', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <svg viewBox="0 0 10 10" width={8} height={8} fill="none" stroke="#fff" strokeWidth={1.5}>
                  <polyline points="1.5 5 4 7.5 8.5 2.5"/>
                </svg>
              </motion.div>
            )}
          </div>
          {/* Texto + barra */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#f0f0ec', marginBottom: '5px', whiteSpace: 'nowrap' }}>
              {error ? 'Error al cargar datos' : isReady ? 'Datos listos' : 'Cargando datos DERA…'}
            </div>
            {isLoading && (
              <>
                <div style={{
                  height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden',
                }}>
                  <motion.div
                    style={{ height: '100%', background: '#2DD4BF', borderRadius: 2 }}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
                <div style={{ fontSize: '10px', color: '#71717a', marginTop: '3px' }}>
                  {progress?.message ?? `${pct}%`}
                </div>
              </>
            )}
            {error && (
              <button
                onClick={() => loadData(true)}
                style={{
                  marginTop: '4px', fontSize: '11px', color: '#2DD4BF',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                }}
              >
                Reintentar →
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default DataLoader;
